/**
 * Cloud Management RAG Service
 * Frontend service for Azure Advisor recommendations and cloud infrastructure analysis
 * This is NOT for OpenAI/AI usage costs - it's for Azure infrastructure cost optimization
 */
import { buildApiUrl } from '../config/apiConfig';

export interface CloudInsight {
  query_type: string;
  ai_response: string;
  insights: string[];
  data_sources: string[];
  confidence_score: number;
  timestamp: string;
}

export interface CloudAnalysisResponse {
  success: boolean;
  query: string;
  response?: string;
  context?: {
    total_recommendations: number;
    cost_recommendations: number;
    high_impact_count: number;
    type: string;
  };
  timestamp: string;
  error?: string;
}

class CloudManagementRAGService {
  constructor() {
    // baseUrl is now handled by buildApiUrl
  }

  private async fetchWithAuth(endpoint: string, options: RequestInit): Promise<Response> {
    const token = sessionStorage.getItem('access_token');
    
    if (!token) {
      throw new Error('No authentication token available');
    }

    const defaultHeaders = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    return fetch(buildApiUrl(endpoint), {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      credentials: 'include',
    });
  }

  async getComprehensiveSummary(provider?: 'azure'|'gcp'): Promise<{ success: boolean; summary: string }>{
    const qp = provider ? `?provider=${provider}` : '';
    const response = await this.fetchWithAuth(`/cloud/comprehensive-summary${qp}`, {
      method: 'GET',
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to get comprehensive summary');
    }
    return { success: true, summary: data.summary || '' };
  }

  async regenerateComprehensiveSummary(provider: 'azure'|'gcp'): Promise<{ success: boolean }>{
    const response = await this.fetchWithAuth(`/cloud/comprehensive-summary/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ provider })
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to regenerate comprehensive summary');
    }
    return { success: true };
  }

  /**
   * Get advanced Azure infrastructure insights using RAG analysis
   */
  async getAdvancedInsights(query: string): Promise<CloudInsight> {
    const response = await this.fetchWithAuth('/cloud/cost-insights', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) {
      throw new Error(`Failed to get cloud insights: ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to generate cloud insights');
    }

    return {
      query_type: data.query_type,
      ai_response: data.ai_response,
      insights: data.insights,
      data_sources: data.data_sources,
      confidence_score: data.confidence_score,
      timestamp: data.timestamp,
    };
  }

  /**
   * Get Azure cost analysis using Advisor recommendations
   */
  async getCloudCostAnalysis(query: string): Promise<CloudAnalysisResponse> {
    const response = await this.fetchWithAuth('/cloud/cost-analysis', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) {
      throw new Error(`Failed to get cloud cost analysis: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Send message to cloud chat with Azure infrastructure awareness
   */
  async sendCloudChatMessage(message: string, subscriptionId?: string): Promise<any> {
    const response = await this.fetchWithAuth('/cloud/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        message,
        subscriptionId 
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to send chat message: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get Azure cost optimization recommendations from Advisor
   */
  async getAzureCostOptimization(): Promise<CloudInsight> {
    return await this.getAdvancedInsights("Show me Azure cost optimization opportunities from Advisor recommendations");
  }

  /**
   * Get security recommendations analysis
   */
  async getSecurityAnalysis(): Promise<CloudInsight> {
    return await this.getAdvancedInsights("Analyze Azure security recommendations and provide improvement suggestions");
  }

  /**
   * Get performance optimization insights
   */
  async getPerformanceAnalysis(): Promise<CloudInsight> {
    return await this.getAdvancedInsights("What are the Azure performance optimization recommendations?");
  }

  /**
   * Get reliability and availability analysis
   */
  async getReliabilityAnalysis(): Promise<CloudInsight> {
    return await this.getAdvancedInsights("Show me Azure reliability and high availability recommendations");
  }

  /**
   * Check if a query is cloud infrastructure related (not AI costs)
   */
  isCloudInfrastructureQuery(query: string): boolean {
    const cloudKeywords = [
      'azure', 'subscription', 'advisor', 'recommendation', 'infrastructure',
      'cost optimization', 'azure cost', 'resource', 'vm', 'storage', 'database',
      'security', 'performance', 'reliability', 'compliance', 'backup',
      'scale', 'availability', 'network', 'firewall', 'rbac'
    ];
    
    const queryLower = query.toLowerCase();
    return cloudKeywords.some(keyword => queryLower.includes(keyword));
  }

  /**
   * Classify cloud query by Well-Architected Framework pillar
   */
  classifyCloudQuery(query: string): string {
    const queryLower = query.toLowerCase();
    
    if (queryLower.includes('security') || queryLower.includes('compliance') || queryLower.includes('rbac') || queryLower.includes('firewall')) {
      return 'security';
    } else if (queryLower.includes('performance') || queryLower.includes('scale') || queryLower.includes('latency')) {
      return 'performance';
    } else if (queryLower.includes('reliability') || queryLower.includes('availability') || queryLower.includes('backup') || queryLower.includes('disaster')) {
      return 'reliability';
    } else if (queryLower.includes('cost') || queryLower.includes('optimize') || queryLower.includes('savings') || queryLower.includes('pricing')) {
      return 'cost';
    } else {
      return 'general';
    }
  }

  /**
   * Get suggested cloud management queries across all Well-Architected Framework pillars
   */
  getSuggestedCloudQueries(): string[] {
    return [
      // Security
      "Review Azure security recommendations and compliance status",
      "Identify security vulnerabilities in my Azure infrastructure",
      "Create security hardening plan for Azure resources",
      
      // Reliability
      "Analyze backup and disaster recovery recommendations",
      "Show me Azure availability zone optimization opportunities",
      "Review Azure resource redundancy and failover strategies",
      
      // Performance
      "Optimize Azure VM performance and sizing recommendations",
      "Analyze Azure storage performance improvements",
      "Review Azure network performance optimization",
      
      // Cost
      "Show me Azure cost optimization opportunities",
      "Identify underutilized Azure resources for cost savings",
      "Create Azure budget optimization strategy",
      "Analyze Azure reserved instance recommendations",
    ];
  }

  /**
   * Format confidence score for display
   */
  formatConfidence(score: number): string {
    if (score >= 0.8) return 'High';
    if (score >= 0.6) return 'Medium';
    if (score >= 0.4) return 'Low';
    return 'Very Low';
  }

  /**
   * Get cloud management quick actions for UI
   */
  getQuickActions(): Array<{category: string, action: string, query: string}> {
    return [
      {
        category: 'Cost',
        action: 'Optimize Costs',
        query: 'Show me Azure cost optimization opportunities'
      },
      {
        category: 'Security', 
        action: 'Security Review',
        query: 'Review Azure security recommendations'
      },
      {
        category: 'Performance',
        action: 'Performance Analysis', 
        query: 'Analyze Azure performance optimization opportunities'
      },
      {
        category: 'Reliability',
        action: 'Backup & DR',
        query: 'Review backup and disaster recovery recommendations'
      }
    ];
  }
}

// Export singleton instance
export const cloudManagementRAGService = new CloudManagementRAGService();
export default cloudManagementRAGService;
